/**
 * A tiny read-through cache for the JSON GETs behind Workflows, Routines and Agents.
 *
 * These pages each refetch everything in a mount effect, so moving between them — or any remount
 * — re-ran the same handful of requests, and each one costs a real query against Postgres. The
 * Workflows page alone has nine GET sites. Nothing here was cached because the app has no query
 * library; this is the smallest thing that removes the repeats without adopting one.
 *
 * Two savings, and the second matters as much as the first:
 *   1. TTL — a response is reused for a few seconds, so a navigation round trip is free.
 *   2. In-flight sharing — N components asking for the same path at the same moment make ONE
 *      request. Three panels mounting together used to mean three identical queries.
 *
 * The danger with any cache is showing stale data after a write, so this is deliberately
 * short-lived AND explicitly invalidated: every mutation path calls `invalidateCache` with the
 * prefix it affected. A cache that is merely time-based would show a renamed workflow under its
 * old name for several seconds, which is worse than the extra query.
 *
 * Not for: anything streaming, anything paginated, or a single item's detail fetch. Lists only.
 */

type Entry = {
  /** Resolved payload, once it has arrived. */
  value?: unknown;
  /** When `value` was stored (epoch ms); absent while the first request is still in flight. */
  storedAt?: number;
  /** The in-flight request, shared by every caller that arrives while it runs. */
  inFlight?: Promise<unknown>;
};

const entries = new Map<string, Entry>();

/** Default freshness. Long enough to cover a navigation, short enough to feel live. */
export const CACHE_TTL_MS = 8_000;

/** Drop everything whose key starts with `prefix` — call after a write that changes it. */
export function invalidateCache(prefix: string): void {
  for (const key of [...entries.keys()]) {
    if (key.startsWith(prefix)) entries.delete(key);
  }
}

/** Drop the whole cache (sign-out, account switch). */
export function clearCache(): void {
  entries.clear();
}

/**
 * Read `key` through the cache, calling `load` only when there is nothing fresh and nothing
 * already on its way.
 *
 * A failed load is NOT cached: the next caller retries. Caching a failure would turn one blip
 * into several seconds of a page insisting it has no workflows — the exact bug this codebase
 * already had from treating a failed load as an empty one.
 */
export async function cached<T>(
  key: string,
  load: () => Promise<T>,
  options?: { readonly ttlMs?: number; readonly now?: () => number },
): Promise<T> {
  const ttl = options?.ttlMs ?? CACHE_TTL_MS;
  const now = options?.now ?? Date.now;
  const existing = entries.get(key);

  if (existing?.storedAt !== undefined && now() - existing.storedAt < ttl) {
    return existing.value as T;
  }
  if (existing?.inFlight) return existing.inFlight as Promise<T>;

  const inFlight = (async () => {
    try {
      const value = await load();
      entries.set(key, { value, storedAt: now() });
      return value;
    } catch (error) {
      entries.delete(key);
      throw error;
    }
  })();
  entries.set(key, { ...(existing ?? {}), inFlight });
  return inFlight;
}

/** Test seam: how many keys are held. */
export function cacheSize(): number {
  return entries.size;
}
